import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { intl, fetchData, HTTP_STATUS, DmsModal, DmsButton } from 'dmsbasereact';
import {
    Box,
    Grid,
    Chip,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Divider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditNoteIcon from '@mui/icons-material/EditNote';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import StorefrontIcon from '@mui/icons-material/Storefront';
import Swal from 'sweetalert2';
import { formatDate, formatDateTime, formatCnpj } from '../../utils/formatters';

const getInvoiceStateInfo = (state) => {
    const val = typeof state === 'object' ? state?.value : state;
    const stateMap = {
        0: { label: intl.get('InvoiceState.Created').d('Created'), color: 'default' },
        1: { label: intl.get('InvoiceState.InProgress').d('In Progress'), color: 'info' },
        2: { label: intl.get('InvoiceState.Received').d('Received'), color: 'success' },
        3: { label: intl.get('InvoiceState.Cancelled').d('Cancelled'), color: 'error' },
        4: { label: intl.get('InvoiceState.Devolution').d('Devolution'), color: 'warning' },
        5: { label: intl.get('InvoiceState.Sent').d('Sent'), color: 'info' },
    };
    return stateMap[val] || { label: intl.get('InvoiceState.Unknown').d('Unknown'), color: 'default' };
};

const EntityCard = ({ icon, label, entity }) => (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            {icon}
            <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
                {label}
            </Typography>
        </Box>
        {entity ? (
            <>
                <Typography variant="body1" fontWeight={600}>
                    {entity.name}
                </Typography>
                {entity.cnpj && (
                    <Typography variant="body2" color="text.secondary">
                        CNPJ: {formatCnpj(entity.cnpj)}
                    </Typography>
                )}
            </>
        ) : (
            <Typography variant="body2" color="text.secondary">NA</Typography>
        )}
    </Paper>
);

export const CheckinDetailModal = ({ open, onClose, invoice, onCheckinConfirmed, onCorrectInvoice }) => {
    const [loading, setLoading] = useState(false);
    const [enrichedInvoice, setEnrichedInvoice] = useState(null);
    const [confirming, setConfirming] = useState(false);

    const loadInvoiceDetails = useCallback(async (invoiceData) => {
        if (!invoiceData) return null;
        try {
            const response = await fetchData(`/invoice_document/${invoiceData.pk}`, 'GET', true);
            if (response.status === HTTP_STATUS.OK_200 && response.data?.data) {
                return response.data.data;
            }
        } catch (error) {
            console.error('Error loading invoice details:', error);
        }
        return null;
    }, []);

    useEffect(() => {
        if (open && invoice) {
            setLoading(true);
            loadInvoiceDetails(invoice).then((enriched) => {
                setEnrichedInvoice(enriched);
                setLoading(false);
            });
        } else if (!open) {
            setEnrichedInvoice(null);
        }
    }, [open, invoice, loadInvoiceDetails]);

    const handleConfirmCheckin = useCallback(async () => {
        if (!invoice) return;

        const items = enrichedInvoice?.invoice_document_items || [];
        const confirmItems = items.map((item) => ({
            batch_pk: item.batch_pk,
            quantity: item.quantity || 0,
        }));

        const result = await Swal.fire({
            title: intl.get('Checkin.ConfirmEntrada').d('Confirm entry'),
            text: intl.get('Checkin.ConfirmMessage').d('Do you want to confirm receipt of this invoice?'),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: intl.get('Confirm').d('Confirm'),
            cancelButtonText: intl.get('Cancel').d('Cancel'),
        });

        if (!result.isConfirmed) return;

        setConfirming(true);
        try {
            const payload = {
                invoice_pk: invoice.pk,
                received_at: new Date().toISOString(),
                items: confirmItems,
            };

            const response = await fetchData('/batch_location/checkin/confirm', 'POST', true, payload);
            if (response.status === HTTP_STATUS.OK_200) {
                Swal.fire({
                    title: intl.get('Success').d('Success'),
                    text: intl.get('Checkin.ConfirmSuccess').d('Entry confirmed successfully!'),
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false,
                });
                onCheckinConfirmed?.();
            } else {
                const errorMsg = response.data?.detail || response.data?.message || intl.get('Checkin.ConfirmError').d('Error confirming entry');
                Swal.fire({
                    title: intl.get('Error').d('Error'),
                    text: errorMsg,
                    icon: 'error',
                });
            }
        } catch (error) {
            console.error('Error confirming checkin:', error);
            Swal.fire({
                title: intl.get('Error').d('Error'),
                text: intl.get('Checkin.ConfirmError').d('Error confirming entry'),
                icon: 'error',
            });
        } finally {
            setConfirming(false);
        }
    }, [invoice, enrichedInvoice, onCheckinConfirmed]);

    const handleCorrectInvoice = useCallback(() => {
        if (!invoice || !onCorrectInvoice) return;
        onClose?.();
        onCorrectInvoice(invoice, enrichedInvoice);
    }, [invoice, enrichedInvoice, onClose, onCorrectInvoice]);

    const data = enrichedInvoice;
    const items = data?.invoice_document_items || [];
    const stateInfo = data ? getInvoiceStateInfo(data.invoice_state) : {};
    const totalQuantity = items.reduce((sum, item) => sum + (item.quantity || 0), 0);

    return (
        <DmsModal
            isVisible={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            {/* Blue styled header with underline */}
            <Box sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: '3px solid #1565c0' }}>
                <Typography variant="h6" sx={{ color: '#1565c0', fontWeight: 700 }}>
                    {`${intl.get('Checkin.ConfirmEntrada').d('Confirm entry')}: ${invoice?.danfe_number || ''}`}
                </Typography>
            </Box>

            <DmsModal.Body>
                <Box sx={{ p: 2.5 }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : data ? (
                        <>
                            {/* Invoice summary row */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
                                <Chip
                                    label={stateInfo.label}
                                    size="small"
                                    color={stateInfo.color}
                                />
                                <Typography variant="body2" color="text.secondary">
                                    {intl.get('IssueDate').d('Issue Date')}: <strong>{formatDate(data.created_on)}</strong>
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {intl.get('Checkin.TotalQuantity').d('Total Quantity')}: <strong>{totalQuantity.toLocaleString('pt-BR')}</strong>
                                </Typography>
                            </Box>

                            {/* Origin / Destination cards */}
                            <Grid container spacing={2} sx={{ mb: 2.5 }}>
                                <Grid item xs={12} sm={6}>
                                    <EntityCard
                                        icon={<LocalShippingIcon sx={{ fontSize: 18, color: '#1565c0' }} />}
                                        label={intl.get('Origin').d('Origin')}
                                        entity={data.owner_entity}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <EntityCard
                                        icon={<StorefrontIcon sx={{ fontSize: 18, color: '#2e7d32' }} />}
                                        label={intl.get('Destination').d('Destination')}
                                        entity={data.destination_entity}
                                    />
                                </Grid>
                            </Grid>

                            <Divider sx={{ mb: 2 }} />

                            {/* Items header */}
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#1565c0' }}>
                                {`${intl.get('InvoiceItems').d('Invoice Items')} (${items.length})`}
                            </Typography>

                            {/* Items table */}
                            {items.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                                    {intl.get('NoItemsFound').d('No items found')}
                                </Typography>
                            ) : (
                                <TableContainer component={Paper} variant="outlined">
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                                                <TableCell sx={{ fontWeight: 700, color: '#1565c0' }}>
                                                    {intl.get('Batch').d('Batch')}
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 700, color: '#1565c0' }}>
                                                    {intl.get('Product').d('Product')}
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 700, color: '#1565c0' }}>
                                                    {intl.get('Expiration').d('Expiration')}
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 700, color: '#1565c0' }} align="center">
                                                    {intl.get('Quantity').d('Quantity')}
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 700, color: '#1565c0' }}>
                                                    {intl.get('SentAt').d('Sent At')}
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {items.map((item, index) => (
                                                <TableRow key={item.pk || index} hover>
                                                    <TableCell>
                                                        <Typography variant="body2" fontWeight={500}>
                                                            {item.batch?.number || 'NA'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        {item.batch?.part?.description || item.batch?.part?.number || 'NA'}
                                                    </TableCell>
                                                    <TableCell>
                                                        {item.batch ? formatDate(item.batch.expiration) : 'NA'}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {(item.quantity || 0).toLocaleString('pt-BR')}
                                                    </TableCell>
                                                    <TableCell>
                                                        {item.sent_at ? formatDateTime(item.sent_at) : 'NA'}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </>
                    ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                            {intl.get('NoDataAvailable').d('No data available')}
                        </Typography>
                    )}
                </Box>
            </DmsModal.Body>
            <DmsModal.Footer>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 2, width: '100%' }}>
                    <DmsButton
                        text={intl.get('Close').d('Close')}
                        onClick={onClose}
                    />
                    {onCorrectInvoice && (
                        <DmsButton
                            icon={<EditNoteIcon />}
                            text={intl.get('Checkin.CorrectInvoice').d('Correct Invoice')}
                            onClick={handleCorrectInvoice}
                            disabled={loading}
                            variant="warning"
                        />
                    )}
                    <DmsButton
                        icon={<CheckCircleIcon />}
                        text={confirming
                            ? intl.get('Processing').d('Processing...')
                            : intl.get('Checkin.ConfirmButton').d('Confirm entry')
                        }
                        onClick={handleConfirmCheckin}
                        disabled={confirming || loading || items.length === 0}
                        variant="primary"
                    />
                </Box>
            </DmsModal.Footer>
        </DmsModal>
    );
};

CheckinDetailModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    invoice: PropTypes.object,
    onCheckinConfirmed: PropTypes.func,
    onCorrectInvoice: PropTypes.func,
};
